import React, { useState } from 'react';
import { PromoBanner } from '@shared/ui/home/PromoBanner';
import { useQuery, useMutation } from '@apollo/client';
import { GET_PROMO_PACKAGE } from '@shared/graphql/queries/subscriptionPackages';
import { CREATE_PAYMENT_INTENT } from '@shared/graphql/mutations/payment';
import { saveCheckoutIntent } from '@shared/ui/checkout/checkout-intent';
import { usePromoTracking } from '../../hooks/usePromoTracking';
import { useAffiliateTracking } from '../../hooks/useAffiliateTracking';
import { useAuth } from '../../contexts/AuthContext';
import { useSubscriptionAccess } from '../../hooks/useSubscriptionAccess';
import analyticsService from '../../services/analytics';
import googleAnalytics from '../../services/googleAnalytics';

interface SubscriptionPackage {
  id: string;
  packageCode: string;
  name: string;
  description: string;
  priceWeekly: number;
  priceMonthly: number;
  priceOneTime?: number;
  billingModel: 'ONE_TIME' | 'SUBSCRIPTION';
  accessDurationDays?: number;
  hasTrackRestrictions?: boolean;
  isPromoPackage?: boolean;
}

interface PromoPackageSectionProps {
  /** 'banner' renders the new look's PromoBanner; the default is the classic card. */
  variant?: 'classic' | 'banner';
}

const PromoPackageSection: React.FC<PromoPackageSectionProps> = ({ variant = 'classic' }) => {
  const { data, loading, error } = useQuery(GET_PROMO_PACKAGE);
  const { getUserDiscountCode, promotion } = usePromoTracking();
  const { getAffiliateForRegistration } = useAffiliateTracking();
  const { isAuthenticated, user } = useAuth();
  const { activeSubscriptions } = useSubscriptionAccess();
  const [createPaymentIntent] = useMutation(CREATE_PAYMENT_INTENT);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);

  // Don't render anything if loading, error, or no promo package
  if (loading || error || !data?.getPromoPackage) {
    return null;
  }

  const promoPackage: SubscriptionPackage = data.getPromoPackage;

  // Check if user has any packages other than default packages (PROMO_PASS for prod, FREE_TRACKS for dev)
  const userHasNonDefaultPackage = activeSubscriptions.some(subscription =>
    subscription.package?.packageCode !== 'PROMO_PASS' &&
    subscription.package?.packageCode !== 'FREE_TRACKS'
  );

  // Don't render if user has any packages other than default packages
  if (isAuthenticated && userHasNonDefaultPackage) {
    console.log('🎯 PromoPackage: User has non-default package, hiding promo. Active packages:', activeSubscriptions.map(sub => sub.package?.packageCode));
    return null;
  }

  const handleCheckout = async () => {
    // Track promo package checkout attempt
    try {
      analyticsService.trackEvent('promo_package_checkout_attempt', {
        pageType: 'home',
        selectedDate: new Date().toISOString().split('T')[0],
        metadata: {
          packageId: promoPackage.id,
          packageName: promoPackage.name,
          packageCode: promoPackage.packageCode,
          billingModel: promoPackage.billingModel,
          isAuthenticated,
          userType: isAuthenticated ? 'authenticated' : 'anonymous'
        }
      });
    } catch (analyticsError) {
      console.error('Analytics error:', analyticsError);
    }

    // If not authenticated, redirect to register
    if (!isAuthenticated) {
      sessionStorage.setItem('intendedPackage', JSON.stringify(promoPackage));
      // after sign-up, carry on to checkout for this package (CheckoutIntentResumer)
      saveCheckoutIntent(promoPackage.packageCode);
      window.location.href = '/register';
      return;
    }

    setCheckoutLoading(true);

    try {
      // For one-time packages that require track selection, redirect to main page
      if (promoPackage.billingModel === 'ONE_TIME' && promoPackage.hasTrackRestrictions) {
        // Don't handle track-restricted one-time packages here - users need to select a track first
        window.location.href = '/';
        return;
      }

      // Get affiliate code if available
      const affiliateCode = getAffiliateForRegistration();
      
      // Get user's assigned discount code (for logged-in users)
      const userDiscountCode = user ? await getUserDiscountCode() : null;
      
      // Get discount code based on user authentication status and available promotions
      const getDiscountCode = () => {
        if (user && userDiscountCode) {
          return userDiscountCode;
        }
        if (promotion?.discountCode) {
          return promotion.discountCode;
        }
        return null;
      };
      
      const discountCode = getDiscountCode();
      
      console.log('🎯 PromoPackage: User logged in:', !!user);
      console.log('🎯 PromoPackage: User discount code:', userDiscountCode);
      console.log('🎯 PromoPackage: Campaign promotion code:', promotion?.discountCode);
      console.log('🎯 PromoPackage: Final discount code:', discountCode);

      // Default to weekly billing cycle for promo packages
      const billingCycle = 'WEEKLY';
      
      const { data } = await createPaymentIntent({
        variables: {
          input: {
            packageIds: [parseInt(promoPackage.id)],
            billingCycle: billingCycle,
            paymentType: 'SUBSCRIPTION',
            affiliateCode: affiliateCode,
            discountCode: discountCode,
          }
        }
      });

      if (data?.createPaymentIntent?.checkoutUrl) {
        // Track successful checkout session creation
        try {
          analyticsService.trackEvent('promo_package_checkout_session_created', {
            pageType: 'home',
            selectedDate: new Date().toISOString().split('T')[0],
            metadata: {
              packageId: promoPackage.id,
              packageName: promoPackage.name,
              billingCycle: billingCycle,
              checkoutUrl: data.createPaymentIntent.checkoutUrl,
              discountCode: discountCode
            }
          });
        } catch (analyticsError) {
          console.error('Analytics error:', analyticsError);
        }

        googleAnalytics.trackBeginCheckout({
          value: promoPackage.priceWeekly,
          currency: 'AUD',
          items: [{
            item_id: promoPackage.packageCode,
            item_name: promoPackage.name,
            price: promoPackage.priceWeekly,
            quantity: 1
          }]
        });

        // Redirect to Stripe Checkout
        window.location.href = data.createPaymentIntent.checkoutUrl;
      } else {
        throw new Error('Failed to create checkout session - no checkout URL received');
      }
    } catch (error) {
      console.error('Error creating promo package checkout:', error);
      
      // Track checkout failure
      try {
        analyticsService.trackEvent('promo_package_checkout_failure', {
          pageType: 'home',
          selectedDate: new Date().toISOString().split('T')[0],
          metadata: {
            packageId: promoPackage.id,
            packageName: promoPackage.name,
            error: error instanceof Error ? error.message : 'Unknown error'
          }
        });
      } catch (analyticsError) {
        console.error('Analytics error:', analyticsError);
      }
      
      alert('Failed to initialize membership. Please try again or visit the pricing page.');
    } finally {
      setCheckoutLoading(false);
    }
  };

  const getDisplayPrice = () => {
    if (promoPackage.billingModel === 'ONE_TIME' && promoPackage.priceOneTime) {
      return `$${promoPackage.priceOneTime}`;
    }
    if (promoPackage.priceWeekly) {
      return `$${promoPackage.priceWeekly}/week`;
    }
    if (promoPackage.priceMonthly) {
      return `$${promoPackage.priceMonthly}/month`;
    }
    return 'Contact for pricing';
  };

  const getAccessDuration = () => {
    if (promoPackage.billingModel === 'ONE_TIME' && promoPackage.accessDurationDays) {
      return ` (${promoPackage.accessDurationDays} days access)`;
    }
    return '';
  };

  if (variant === 'banner') {
    const oneOff = promoPackage.billingModel === 'ONE_TIME';
    const days = promoPackage.accessDurationDays;
    return (
      <PromoBanner
        name={promoPackage.name}
        kind={oneOff ? 'One-off package' : 'Subscription'}
        price={getDisplayPrice()}
        priceNote={oneOff && days ? `${days} ${days === 1 ? 'day' : 'days'} access` : undefined}
        description={promoPackage.description}
        busy={checkoutLoading}
        onBuy={handleCheckout}
      />
    );
  }

  return (
    <>
      <style>{`
        @media (max-width: 768px) {
          .promo-package-card {
            padding: 12px !important;
          }
          .promo-package-content {
            flex-direction: column !important;
            align-items: stretch !important;
          }
          .promo-package-title {
            font-size: 16px !important;
          }
          .promo-package-price {
            font-size: 20px !important;
          }
          .promo-package-button-wrapper {
            margin-left: 0 !important;
            margin-top: 12px !important;
          }
          .promo-package-button {
            width: 100%;
          }
        }
        @media (max-width: 480px) {
          .promo-package-card {
            padding: 10px !important;
          }
          .promo-package-title {
            font-size: 15px !important;
          }
        }
      `}</style>
      <div className="bg-white border-2 border-slate-800 rounded-xl p-4 shadow-sm promo-package-card">
        <div className="flex items-center justify-between promo-package-content">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-yellow-500 text-lg">👑</span>
            <h3 className="text-lg font-bold text-slate-800 promo-package-title">{promoPackage.name}</h3>
            <span className="bg-slate-800 text-yellow-400 text-xs font-semibold px-2.5 py-0.5 rounded-full">
              {promoPackage.billingModel === 'ONE_TIME' ? 'One-off Package' : 'Subscription'}
            </span>
          </div>

          <div className="flex items-baseline gap-2 mb-2">
            <span className="font-bold text-slate-800 text-2xl promo-package-price">
              {promoPackage.billingModel === 'ONE_TIME' && promoPackage.priceOneTime
                ? `$${promoPackage.priceOneTime}`
                : promoPackage.priceWeekly
                ? `$${promoPackage.priceWeekly}`
                : promoPackage.priceMonthly
                ? `$${promoPackage.priceMonthly}`
                : 'Contact for pricing'}
            </span>
            <span className="text-slate-500 text-sm">
              {promoPackage.billingModel === 'ONE_TIME'
                ? promoPackage.accessDurationDays
                  ? `(${promoPackage.accessDurationDays} days access)`
                  : ''
                : promoPackage.priceWeekly
                ? '/week'
                : promoPackage.priceMonthly
                ? '/month'
                : ''}
            </span>
          </div>

          {promoPackage.description && (
            <div className="text-slate-600 text-sm">
              <p className={isDescriptionExpanded ? '' : 'line-clamp-1'}>
                {promoPackage.description}
              </p>
              {promoPackage.description.length > 100 && (
                <button
                  onClick={() => setIsDescriptionExpanded(!isDescriptionExpanded)}
                  className="text-slate-800 hover:text-slate-600 text-xs mt-1 font-medium underline focus:outline-none"
                >
                  {isDescriptionExpanded ? 'Show less' : 'Show more'}
                </button>
              )}
            </div>
          )}
        </div>

        <div className="flex-shrink-0 ml-4 promo-package-button-wrapper">
          <button
            onClick={handleCheckout}
            className="bg-slate-800 hover:bg-slate-700 text-yellow-400 font-bold py-2.5 px-5 rounded-lg text-sm shadow-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed promo-package-button"
            disabled={checkoutLoading}
          >
            {checkoutLoading ? (
              <div className="flex items-center gap-2">
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-yellow-400 border-t-transparent"></div>
                <span>Loading...</span>
              </div>
            ) : (
              isAuthenticated ? 'Purchase Now' : 'Get Started'
            )}
          </button>
        </div>
      </div>
    </div>
    </>
  );
};

export default PromoPackageSection;